/**
 * Dynamic Island module configuration: master toggle and layout metrics.
 */
export const DYNAMIC_ISLAND = {
    /** Master switch — false hides the slot and zeroes layout offsets. */
    "enabled": true,

    /** Fixed height (px) of the island chrome, aligned with the map vehicle popup. */
    "heightPx": 42,

    /**
     * Horizontal gap (%) between the island and each adjacent FAB on narrow viewports.
     * Percentage is relative to the FAB row container width.
     */
    "toFabGapPercentMobile": 2,

    /**
     * Horizontal gap (%) between the island and each adjacent FAB from
     * `fabGapDesktopMinWidthPx` upward (wider screens get more breathing room).
     */
    "toFabGapPercentDesktop": 8,

    /** Viewport min-width (px) where `toFabGapPercentDesktop` applies. Matches Tailwind `md`. */
    "fabGapDesktopMinWidthPx": 768,

    /** Default horizontal scroll distance (px) for generic island arrow steppers. */
    "horizontalScrollStepPx": 120,

    /** Motion-driven shell and variant animation settings. */
    "animations": {
        /** Master switch — false keeps instant mount/unmount (pre-Motion behavior). */
        "enabled": true,

        /** Default spring; variants may override via registry. */
        "spring": {
            "type":      "spring" as const,
            "stiffness": 420,
            "damping":   32,
            "mass":      0.8,
        },

        /** Max width of the morphing pill (% of FAB row inner width). */
        "maxWidthPercent": 62,

        "shell": {
            "enter": {
                "opacity": [ 0, 1 ] as const,
                "scaleX":  [ 0.72, 1 ] as const,
                "scaleY":  [ 0.88, 1 ] as const,
            },
            "exit": {
                "opacity": [ 1, 0 ] as const,
                "scaleX":  [ 1, 0.85 ] as const,
                "scaleY":  [ 1, 0.9 ] as const,
            },

            /** Kind switch — directional: outgoing slides, incoming expands from center. */
            "kindSwitch": {
                "outgoing": {
                    "opacity": [ 1, 0 ] as const,
                    "x":       [ 0, -12 ] as const,
                    "scale":   [ 1, 0.92 ] as const,
                },
                "incoming": {
                    "opacity": [ 0, 1 ] as const,
                    "scale":   [ 0.85, 1 ] as const,
                    "originX": 0.5,
                },
            },
            "staggerMs": 60,
        },

        /**
         * Vehicle realtime — in-place morph when the user selects another bus (tripId change).
         * Shell colors interpolate; pill squeezes then springs open; row content handoffs vertically.
         */
        "vehicleSessionSwitch": {
            "colorDurationS":        0.32,
            "contentSlidePx":        8,
            "contentScaleFrom":      0.94,
            "contentExitDurationS":  0.16,
            "pillScaleXFrom":        0.96,
            "pillScaleYFrom":        0.9,
            "pillCompressDurationS": 0.1,
            "contentSpring":         {
                "type":      "spring" as const,
                "stiffness": 520,
                "damping":   34,
                "mass":      0.72,
            },
            "pillReleaseSpring": {
                "type":      "spring" as const,
                "stiffness": 480,
                "damping":   28,
                "mass":      0.75,
            },
        },

        /** Debounce before RT text micro-pulse (delay, headsign, stop). */
        "rtTextPulseDebounceMs": 400,

        /** Capacitor haptics on open/close; no-op on web and when reduced motion. */
        "haptics": {
            "onOpen":  true,
            "onClose": true,
        },
    },
} as const;
